// Opaque key used everywhere to enforce type safety, but preserves a stable string in runtime
type Key<K extends string = string> = string & { readonly __type: `Key:${K}` };

type AnyKey<C> = Key<Kind<C>>;
type AnySub = (changes: any[]) => void;
type AnyTypeSub = (change: any) => void;

// convenience type to avoid repeating "keyof Config & string" everywhere
type Kind<Config> = keyof Config & string;

// Extract the change type names from a changes object
type ChangeType<Changes extends Record<string, unknown>> = keyof Changes & string;

// Convert changes map to union type for coarse-grained subscriptions
type ChangesToUnion<Changes extends Record<string, unknown>> = {
  [K in keyof Changes]: { type: K; payload: Changes[K] };
}[keyof Changes];

/**
 * A strongly-typed registry for targeted component updates.
 * Each "kind" (e.g., cell, row, column) defines both its key structure
 * and the set of update event shapes ("changes") it can receive.
 *
 * @template Config A map of component kinds → their key structure and change types
 */
export type PingRegistry<
  Config extends Record<string, { key: unknown; changes: Record<string, unknown> }>
> = {
  /**
   * Generate a unique key for the given kind and parts.
   *
   * The key is created by encoding the parts using the encoder function provided in the
   * config. For example, `key('cell', { x: 0, y: 0 })` might return `"cell-0-0"`.
   *
   * These keys are used to identify subscription targets and notification recipients.
   */
  key<K extends Kind<Config>>(kind: K, parts: Config[K]['key']): Key<K>;

  /**
   * Subscribe to all change types for a given key.
   *
   * This is a coarse-grained subscription that receives every change for the key,
   * regardless of type. For example, if a cell has both `presence` and `selected` changes,
   * this subscriber will be notified of both.
   *
   * Changes are batched and delivered together in a single array in the next microtask.
   * If you only care about specific change types, use `subscribeTo` instead to avoid
   * unnecessary re-renders.
   */
  subscribe<K extends Kind<Config>>(
    key: Key<K>,
    sub: (changes: Array<ChangesToUnion<Config[K]['changes']>>) => void
  ): () => void;

  /**
   * Create a fine-grained subscription to a specific change type for a key.
   *
   * Unlike `subscribe`, which receives all change types for a key, this method allows
   * you to subscribe to only one type of change. For example, if a cell can have both
   * `selected` and `value` changes, calling `subscribeTo(cellKey, 'value', callback)`
   * will only trigger when the value changes, not when the selection state changes.
   *
   * This is useful for performance optimization when a component only cares about
   * specific update types and wants to avoid unnecessary re-renders.
   */
  subscribeTo<K extends Kind<Config>, T extends ChangeType<Config[K]['changes']>>(
    key: Key<K>,
    type: T,
    sub: (payload: Config[K]['changes'][T]) => void
  ): () => void;

  /**
   * Notify subscribers of a change to a given key. The change is queued and delivered
   * in the next microtask, allowing multiple notifications to be batched together.
   * Changes are coalesced by type (last-write-wins) before delivery.
   *
   * Example: `notify(key('cell', { x: 0, y: 0 }), 'value', 100)`
   */
  notify<K extends Kind<Config>, T extends ChangeType<Config[K]['changes']>>(
    key: Key<K>,
    type: T,
    payload: Config[K]['changes'][T]
  ): void;
};

export function createPingRegistry<
  Config extends Record<string, { key: unknown; changes: Record<string, unknown> }>
>(config: {
  encode: { [K in Kind<Config>]: (parts: Config[K]['key']) => string };
  onError?: (error: Error, context: { key: string; changeType: string }) => void;
}): PingRegistry<Config> {
  let scheduled = false;

  const subscriptions = {
    /**
     * Coarse-grained subscriptions by key.
     *
     * Maps encoded keys (e.g., `cell-0-0`) to subscribers that receive *all* change types
     * for that key. For example, a subscriber on `cell-0-0` will be notified of both
     * `selected` and `value` changes.
     */
    byKey: new Map<AnyKey<Config>, Set<AnySub>>(),

    /**
     * Fine-grained subscriptions by change type.
     *
     * Maps encoded keys to change types to their subscribers. For example, subscribers
     * to `selected` on `cell-0-0` receive only selection changes, not all
     * changes for that cell. This allows components to subscribe to specific update types
     * and avoid unnecessary re-renders when unrelated properties change.
     */
    byChangeType: new Map<AnyKey<Config>, Map<string, Set<AnyTypeSub>>>(),
  };

  /**
   * A map containing all the pending flushes.
   */
  const pending: Map<AnyKey<Config>, Map<string, any>> = new Map();

  /**
   * Schedule a flush in the microtask queue if one is not already scheduled.
   * This ensures that if multiple notifies occur in the same tick, they are batched
   * together and delivered as a single update. For each key, changes are coalesced
   * by type (last-write-wins), but all change types for a key are delivered together.
   * This prevents unnecessary re-renders by ensuring that if x components are subscribed
   * to a given key, they all receive their updates in a single batch rather than
   * triggering x separate re-renders.
   */
  function scheduleFlush(): void {
    if (scheduled) {
      return;
    }

    scheduled = true;

    queueMicrotask(flush);
  }

  /**
   * Flush all pending notifications to subscribers.
   *
   * Takes a snapshot of pending changes, clears the queue, and delivers batched updates.
   * Fine-grained (by change type) subscribers are notified first, followed by coarse-grained
   * (by key) subscribers. Errors during notification are caught and logged to avoid breaking
   * other subscribers.
   */
  function flush(): void {
    scheduled = false;

    if (pending.size === 0) {
      return;
    }

    // Take a snapshot of pending changes and clear it immediately
    // In the case that other entries are added to pending, this
    // frees up the structure to accept more.
    const batches = Array.from(pending.entries());

    pending.clear();

    for (const [key, byType] of batches) {
      const subsByKey = subscriptions.byKey.get(key);
      const subsByChangeType = subscriptions.byChangeType.get(key);

      // Nothing to do if no subs exist
      if (
        (!subsByKey || subsByKey.size === 0) &&
        (!subsByChangeType || subsByChangeType.size === 0)
      ) {
        continue;
      }

      // First, send off the granular changes, by change type
      if (subsByChangeType && subsByChangeType.size > 0) {
        for (const [changeType, payload] of byType.entries()) {
          const bucket = subsByChangeType.get(changeType);

          if (!bucket || bucket.size === 0) {
            continue;
          }

          // Snapshot subscribers to avoid mutation during delivery
          const subs = [...bucket];

          for (const sub of subs) {
            try {
              sub(payload);
              // eslint-disable-next-line canvas/no-swallow-errors-in-catch
            } catch (error) {
              const err = error instanceof Error ? error : new Error(String(error));

              if (config.onError) {
                config.onError(err, {
                  key,
                  changeType,
                });
              }
            }
          }
        }
      }

      // Then the coarse, by key
      if (subsByKey && subsByKey.size > 0) {
        // Convert to union type with { type, payload } structure
        const coalesced = Array.from(byType.entries()).map(([type, payload]) => ({
          type,
          payload,
        }));
        const subs = [...subsByKey];

        for (const sub of subs) {
          try {
            sub(coalesced);
            // eslint-disable-next-line canvas/no-swallow-errors-in-catch
          } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            const changeTypes = coalesced.map((c) => c.type).join(', ');

            if (config.onError) {
              config.onError(err, {
                key,
                changeType: changeTypes,
              });
            }
          }
        }
      }
    }
  }

  function key<K extends Kind<Config>>(kind: K, parts: Config[K]['key']): Key<K> {
    const encoder = config.encode[kind];
    const id = encoder(parts);

    return id as Key<K>;
  }

  function subscribe<K extends Kind<Config>>(
    key: Key<K>,
    subscriber: (changes: Array<ChangesToUnion<Config[K]['changes']>>) => void
  ) {
    let bucket = subscriptions.byKey.get(key);

    if (!bucket) {
      bucket = new Set<AnySub>();

      subscriptions.byKey.set(key, bucket);
    }

    bucket.add(subscriber);

    return cleanupSubscribe(key, subscriber);
  }

  /**
   * Returns a cleanup function that removes the subscriber from the key.
   * If this was the last subscriber for the key, the key is removed entirely
   * for memory management.
   */
  function cleanupSubscribe<K extends Kind<Config>>(
    key: Key<K>,
    subscriber: (changes: Array<ChangesToUnion<Config[K]['changes']>>) => void
  ) {
    return () => {
      const current = subscriptions.byKey.get(key);

      if (!current) {
        return;
      }

      // remove the individual subscriber
      current.delete(subscriber);

      // if there are no more subscriptions for this key, remove it
      // for memory management.
      if (current.size === 0) {
        subscriptions.byKey.delete(key);
      }
    };
  }

  function subscribeTo<
    K extends Kind<Config>,
    T extends ChangeType<Config[K]['changes']>
  >(
    key: Key<K>,
    changeType: T,
    sub: (payload: Config[K]['changes'][T]) => void
  ): () => void {
    // Get or create the by-key map for the key
    let byKeyChangeTypeMap = subscriptions.byChangeType.get(key);

    if (!byKeyChangeTypeMap) {
      byKeyChangeTypeMap = new Map<string, Set<AnyTypeSub>>();

      subscriptions.byChangeType.set(key, byKeyChangeTypeMap);
    }

    // Get or create the bucket (Set) for this specific type
    let bucket = byKeyChangeTypeMap.get(changeType);

    if (bucket === undefined) {
      bucket = new Set<AnyTypeSub>();

      byKeyChangeTypeMap.set(changeType, bucket);
    }

    bucket.add(sub);

    return cleanupSubscribeTo(key, changeType, sub);
  }

  /**
   * Returns the cleanup method for subscribeTo which removes the subscription
   * for the specified key and change type. If this was the last subscription
   * for that type or key, the internal maps are pruned for memory management.
   */
  function cleanupSubscribeTo<
    K extends Kind<Config>,
    T extends ChangeType<Config[K]['changes']>
  >(key: Key<K>, changeType: T, sub: (payload: Config[K]['changes'][T]) => void) {
    return () => {
      const byKeyChangeTypeMap = subscriptions.byChangeType.get(key);

      if (byKeyChangeTypeMap === undefined) {
        return;
      }

      const bucket = byKeyChangeTypeMap.get(changeType);

      if (bucket === undefined) {
        return;
      }

      bucket.delete(sub);

      // If this type bucket is empty, remove it
      if (bucket.size === 0) {
        byKeyChangeTypeMap.delete(changeType);
      }

      // If the inner map is now empty, remove the key entirely
      if (byKeyChangeTypeMap.size === 0) {
        subscriptions.byChangeType.delete(key);
      }
    };
  }

  function notify<K extends Kind<Config>, T extends ChangeType<Config[K]['changes']>>(
    key: Key<K>,
    type: T,
    payload: Config[K]['changes'][T]
  ): void {
    const bucket = subscriptions.byKey.get(key);
    const typeSubscriptions = subscriptions.byChangeType.get(key);

    // If no subscriptions at all, bail early
    if (
      (!bucket || bucket.size === 0) &&
      (!typeSubscriptions || typeSubscriptions.size === 0)
    ) {
      return;
    }

    // Always coalesce by type per key (last-write-wins)
    let byType = pending.get(key);

    if (!byType) {
      byType = new Map<string, any>();

      pending.set(key, byType);
    }

    // Overwrite existing change for same type
    byType.set(type as string, payload);

    // Schedule a microtask flush if one isn't already scheduled
    scheduleFlush();
  }

  return {
    key,
    subscribe,
    subscribeTo,
    notify,
  };
}
